import { Card, Table } from "antd";
import { ArrowRightOutlined, ProfileOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { RootState } from "../../../core/store";
import { allPermissions } from "../../../shared/constants/constants";
import { API_MISSING_STAFF_FIELDS, ROUTE_MISSING_STAFF_FIELDS } from "../../../shared/routes/private.routes";
import { accessAllowed } from "../../../shared/utils/utils";
import { MissingFieldSummary, useMissingFieldsResource } from "../missing-fields";
import DashboardLoadError from "./dashboard-load-error.component";

export default function MissingFieldsSummary({ refreshKey = 0 }: { refreshKey?: number }) {
  const permissions = useSelector((state: RootState) => state.auth.myPermissions);
  const canView = accessAllowed(permissions || [], allPermissions.VIEW_STAFF);
  const { data, loading, error, reload } = useMissingFieldsResource<MissingFieldSummary[]>(API_MISSING_STAFF_FIELDS, canView, refreshKey);
  if (!canView) return null;

  return <Card title={
    <div className="dashboard-alert-title">
      <div className="dashboard-alert-title-main">
        <span className="missing-fields-heading"><ProfileOutlined /> Missing Fields</span>
        <span className="dashboard-alert-window">Active and notice-period staff. Completed shows the percentage with a value recorded.</span>
      </div>
    </div>
  } className="dashboard-alert-card dashboard-missing-fields-card" variant="borderless">
    {error ? <DashboardLoadError message="Unable to load missing fields." onRetry={reload} /> :
      <Table<MissingFieldSummary>
        rowKey="field"
        loading={loading}
        locale={{ emptyText: loading ? null : "No missing-field summary available." }}
        dataSource={data || []}
        pagination={false}
        size="small"
        tableLayout="fixed"
        scroll={{ x: 700 }}
        columns={[
          { title: "Field", dataIndex: "label", width: "25%", render: (value: string) => <span className="missing-fields-label">{value}</span> },
          { title: "Total Staff", dataIndex: "total", align: "center", render: (value: number) => <span className="missing-fields-count missing-fields-count-total">{value}</span> },
          { title: "Filled", dataIndex: "filled", align: "center", render: (value: number) => <span className="missing-fields-count missing-fields-count-filled">{value}</span> },
          { title: "Completed", dataIndex: "completed", align: "center", render: (value: number) => (
            <div className={`missing-fields-completion ${value === 100 ? "is-complete" : value < 50 ? "needs-attention" : ""}`}>
              <span>{value}%</span>
              <span className="missing-fields-progress" aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} /></span>
            </div>
          ) },
          { title: "Missing", dataIndex: "missing", align: "center", render: (value: number) => <span className={`missing-fields-count ${value ? "missing-fields-count-missing" : "missing-fields-count-filled"}`}>{value}</span> },
          { title: "Action", align: "center", render: (_: unknown, row) => <Link className="missing-fields-view-all" to={ROUTE_MISSING_STAFF_FIELDS.replace(":field", row.field)}>View All <ArrowRightOutlined aria-hidden="true" /></Link> },
        ]}
      />}
  </Card>;
}
